import { track } from "@/lib/analytics"
import { Link } from "react-router-dom"
import type { Ref } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { deleteMatterFile, linkFile } from "../api"
import { shortDate } from "../answer/format"
import type { MatterContents, MatterFile, MatterSession } from "../contract"
import { originLabel } from "../matter/copy"
import { fileState, useProcessingPoll } from "../matter/fileStatus"
import { MatterSessions, SessionRows, rowsOf, type ExtraRow } from "../matter/MatterSessions"
import { cleanName, demandLine, longTitle, placeRows, shelfOf, shortTitle, type Group, type Member } from "../matter/proceedings"
import { RowMenu, type MenuItem } from "../matter/RowMenu"
import { SessionGuide } from "../matter/StartHere"
import { WorkspaceDrop, sizeOf, type DropHandle } from "../matter/WorkspaceDrop"
import "./workspace.css"

/** One line of a timeline: what a document is (or its name), its date, its state, and a menu. */
interface Line { file: MatterFile; label: string; date: string; pid: string | null; hint?: boolean }

interface Props {
  caseId: string
  title: string
  contents: MatterContents
  /** Every session and reading; empty in stage B. */
  sessions: MatterSession[]
  extra: ExtraRow[]
  onChanged: () => void
  tab: "sessions" | "files"
  /** The Add files control's handle: the page hands dropped files to it. */
  dropRef: Ref<DropHandle>
  /** Files are being dragged over the page: the drop strip lights up. */
  dragging: boolean
}

/**
 * The workspace, organised around its proceedings: what needs attention, then one section per notice
 * (its papers as a timeline, the sessions started on it), then everything that serves them all.
 */
export function Workspace({ caseId, title, contents, sessions, extra, onChanged, tab, dropRef, dragging }: Props) {
  const files = contents.files
  const rows = useMemo(() => rowsOf(sessions, extra), [sessions, extra])
  const shelf = useMemo(() => shelfOf(contents), [contents])
  const { by, placed } = useMemo(() => placeRows(rows, shelf.groups), [rows, shelf.groups])
  const [error, setError] = useState<string | null>(null)
  const [asking, setAsking] = useState<string | null>(null)
  const [moving, setMoving] = useState<string | null>(null)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const root = useRef<HTMLDivElement>(null)
  const cancel = useRef<HTMLButtonElement>(null)
  const removing = useRef(false)
  const refocus = useRef<string | "add" | null>(null)

  // A file the backend is still reading is re-fetched with backoff until it is read or failed.
  useProcessingPoll(files, onChanged)

  useEffect(() => {
    if (asking) { cancel.current?.focus(); return }
    const to = refocus.current
    refocus.current = null
    if (!to) return
    const add = document.querySelector<HTMLElement>(".wsd-add")
    if (to === "add") add?.focus()
    else (root.current?.querySelector<HTMLElement>(`[data-doc="${to}"] .rm-btn`) ?? add)?.focus()
  }, [asking])

  const link = useCallback(async (f: MatterFile, to: string | null) => {
    setError(null)
    setMoving(null)
    try {
      await linkFile(caseId, f.doc_id, to)
      track("matter_file_linked", { linked: to !== null })
      onChanged()
    } catch {
      setError(`Could not ${to ? "move" : "unlink"} ${cleanName(f.name)}. Try again.`)
    }
  }, [caseId, onChanged])

  const remove = async (f: MatterFile) => {
    if (removing.current) return
    removing.current = true
    setError(null)
    const i = files.findIndex((x) => x.doc_id === f.doc_id)
    const near = files[i + 1] ?? files[i - 1]
    try {
      await deleteMatterFile(caseId, f.doc_id)
      track("matter_file_deleted")
    } catch {
      setError(`Could not delete ${f.name}. Try again.`)
      return
    } finally {
      removing.current = false
    }
    refocus.current = near ? near.doc_id : "add"
    setAsking(null)
    onChanged()
  }

  const toggle = (id: string) => setOpen((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n })

  const sessionsTotal = contents.sessions_total
  const row = ({ file: f, label, date, pid, hint }: Line) => {
    const st = fileState(f)
    const expanded = open.has(f.doc_id)
    const others = shelf.groups.filter((g) => g.proceeding.id !== pid)
    const items: MenuItem[] = [
      ...(pid && others.length ? [{ key: "move", label: "Move to…", onSelect: () => setMoving(f.doc_id) }] : []),
      ...(pid ? [{ key: "unlink", label: "Unlink", onSelect: () => void link(f, null) }] : []),
      { key: "delete", label: "Delete", danger: true, onSelect: () => setAsking(f.doc_id) },
    ]
    return (
      <li key={f.doc_id} className="wsx-row" data-doc={f.doc_id}>
        <div className="wsx-line">
          <button type="button" className="wsx-name" aria-expanded={expanded} title={f.name} onClick={() => toggle(f.doc_id)}>{label}</button>
          {date ? <span className="wsx-date">{shortDate(date)}</span> : null}
          <span className={`wsx-state ${st.phase}`} role={st.phase === "failed" ? "alert" : undefined}>{st.label}</span>
          {sizeOf(f.size_bytes) ? <span className="wsx-date">{sizeOf(f.size_bytes)}</span> : null}
          <RowMenu label={`More for ${label}`} items={items} className="wsx-menu" />
        </div>
        {f.summary && !expanded ? <p className="wsf-sum">{f.summary}</p> : null}
        {hint && shelf.groups.length ? (
          <div className="wsx-seg-row" role="group" aria-label={`Attach ${label} to`}>
            <span>Attach to</span>
            {shelf.groups.map((g) => <button key={g.proceeding.id} type="button" className="wsx-seg" onClick={() => void link(f, g.proceeding.id)}>{shortTitle(g.proceeding)}</button>)}
          </div>
        ) : null}
        {moving === f.doc_id ? (
          <div className="wsx-seg-row" role="group" aria-label={`Move ${label} to`}>
            <span>Move to</span>
            {others.map((g) => <button key={g.proceeding.id} type="button" className="wsx-seg" onClick={() => void link(f, g.proceeding.id)}>{shortTitle(g.proceeding)}</button>)}
            <button type="button" className="wsx-link" onClick={() => setMoving(null)}>Cancel</button>
          </div>
        ) : null}
        {expanded ? (
          <div className="wsx-detail">
            <p className="wsx-fn">{f.name}</p>
            {f.summary ? <p className="wsx-sum">{f.summary}</p> : null}
            <p className="wsx-meta">
              <span>{originLabel(f.origin)}</span>
              {sizeOf(f.size_bytes) ? <span>{sizeOf(f.size_bytes)}</span> : null}
              <span>{st.label}</span>
            </p>
            {st.detail ? <p className="wsx-why">{st.detail}</p> : null}
            {f.also_in?.length ? <ul className="wsx-also">{f.also_in.map((o, i) => <li key={i}>{`also ${originLabel(o)}`}</li>)}</ul> : null}
          </div>
        ) : null}
        {asking === f.doc_id ? (
          <div className="wsx-ask">
            <span>{`Delete ${f.name}? It is removed from ${sessionsTotal} ${sessionsTotal === 1 ? "session" : "sessions"} in this workspace.`}</span>
            <button type="button" className="wsx-yes" onClick={() => void remove(f)}>Delete</button>
            <button ref={cancel} type="button" onClick={() => { refocus.current = f.doc_id; setAsking(null) }}>Cancel</button>
          </div>
        ) : null}
      </li>
    )
  }

  const readingNow = shelf.reading
  const attention = rows.filter((r) => r.attention)
  const hasAttention = attention.length + shelf.unlinked.length + readingNow.length > 0
  const sections = shelf.groups
  const evidence = [...shelf.evidence, ...shelf.failed]
  const scnOf = useMemo(() => {
    const m: Record<string, string> = {}
    for (const g of sections) for (const r of by.get(g.proceeding.id) ?? []) m[r.key] = shortTitle(g.proceeding)
    return m
  }, [sections, by])
  const lines = (g: Group): Line[] => g.members.map((m: Member) => ({ file: m.file, label: m.label, date: m.date, pid: g.proceeding.id }))
  const flat = (list: MatterFile[], hint?: boolean): Line[] => list.map((file) => ({ file, label: cleanName(file.name), date: file.date, pid: null, hint }))

  const attentionBlock = hasAttention ? (
    <section className="wsx-attn" aria-label="Needs attention">
      <h2 className="wsx-h3">Needs attention</h2>
      <ul>
        {attention.map((r) => (
          <li key={r.key}><Link to={r.to}>{r.title}</Link><span className="wsx-ask-s">{r.status}</span></li>
        ))}
        {shelf.unlinked.map((f) => (
          <li key={f.doc_id} className="wsx-un">
            <span>{`${labelFor(f)}${f.date ? ` · ${shortDate(f.date)}` : ""}: which notice is this for?`}</span>
            {sections.length ? (
              <span className="wsx-seg-row" role="group" aria-label={`Attach ${labelFor(f)} to`}>
                {sections.map((g) => <button key={g.proceeding.id} type="button" className="wsx-seg" onClick={() => void link(f, g.proceeding.id)}>{shortTitle(g.proceeding)}</button>)}
              </span>
            ) : null}
            <span className="wsx-hint">Nira couldn't tell which notice this belongs to.</span>
          </li>
        ))}
        {readingNow.map((f) => {
          const pct = f.pages_total > 0 ? Math.round((Math.min(f.pages_done, f.pages_total) / f.pages_total) * 100) : 0
          return (
            <li key={f.doc_id} className="wsx-rd">
              <span>{`Reading ${cleanName(f.name)}`}<span className="wsx-ask-s">{fileState(f).label.replace(/^Reading · ?/, "")}</span></span>
              <span className="wsx-bar" role="progressbar" aria-label={`Reading ${cleanName(f.name)}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><span style={{ width: `${pct}%` }} /></span>
            </li>
          )
        })}
      </ul>
    </section>
  ) : null

  const list = (ls: Line[]) => <ul className="wsf-rows">{ls.map(row)}</ul>
  const group = (key: string, heading: string, sub: string, ls: Line[]) => (
    <section key={key} className="wsf-group" aria-label={heading}>
      <h3 className="wsf-gh"><span>{heading}</span>{sub ? <span className="wsf-sub">{sub}</span> : null}</h3>
      {list(ls)}
    </section>
  )

  return (
    <div ref={root} className="wsx">
      {error ? <p className="wsx-err" role="alert">{error}</p> : null}
      {contents.unavailable.length ? <p className="wsx-note" role="status">Some files can't be shown right now.</p> : null}

      {tab === "sessions" ? (
        <div role="tabpanel" id="wsx-panel-sessions" aria-labelledby="wsx-tab-sessions" className="wsx-panel">
          {attentionBlock}
          {rows.length ? (
            <MatterSessions caseId={caseId} sessions={sessions} total={sessions.length} extra={extra} heading="" scnOf={scnOf} />
          ) : (
            <SessionGuide caseId={caseId} title={title} proceedings={contents.proceedings} files={files} showAction={false} />
          )}
        </div>
      ) : (
        <div role="tabpanel" id="wsx-panel-files" aria-labelledby="wsx-tab-files" className="wsx-panel">
          <section className="wsf" aria-label="Files">
            <div className="wsf-head">
              <h2 className="wsf-h">Files <span className="wsx-n">{files.length}</span></h2>
              <WorkspaceDrop ref={dropRef} caseId={caseId} variant="compact" onChanged={onChanged} />
            </div>
            <div className={`wsf-strip${dragging ? " over" : ""}`}>Drop files here</div>
            <div className="wsf-body" tabIndex={0} aria-label="Files list">
              {sections.map((g) => group(g.proceeding.id, longTitle(g.proceeding), [g.proceeding.date ? shortDate(g.proceeding.date) : "", demandLine(g.proceeding)].filter(Boolean).join(" · "), lines(g)))}
              {shelf.unlinked.length ? group("unlinked", "Not linked", "", flat(shelf.unlinked, true)) : null}
              {shelf.reading.length ? group("reading", "Being read", "", flat(shelf.reading)) : null}
              {evidence.length ? group("evidence", sections.length ? "Evidence" : "Documents", "", flat(evidence)) : null}
              {!files.length ? <p className="wsx-none">No files yet.</p> : null}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

/** A file's role word for the attention list: "Reply", "Hearing record", else its name. */
function labelFor(f: MatterFile): string {
  const r = f.role ?? (f.kind === "reply" ? "reply" : f.kind === "hearing" ? "hearing" : f.kind === "order" ? "order" : null)
  return r === "reply" ? "Reply" : r === "hearing" ? "Hearing record" : r === "order" ? "Order" : r === "appeal" ? "Appeal" : cleanName(f.name)
}
